import React from 'react';
import { Product } from '../../types';
import ProductCard from './ProductCard';
import LoadingSpinner from '../ui/LoadingSpinner';

interface ProductGridProps {
  products: Product[];
  isLoading?: boolean;
  error?: any;
  gridClassName?: string;
}

const DEFAULT_GRID_CLASS = 'grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4';

const ProductGrid: React.FC<ProductGridProps> = ({
  products,
  isLoading = false,
  error,
  gridClassName,
}) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center p-3xl bg-[var(--color-bg)] rounded-sm border border-border">
        <div className="text-4xl mb-lg text-error">
          ⚠️
        </div>
        <h3 className="font-serif text-2xl text-primary mb-md">
          Unable to Load Products
        </h3>
        <p className="text-text-secondary mb-xl max-w-md mx-auto">
          We encountered an error while loading the products. Please try refreshing the page.
        </p>
        <button
          onClick={() => window.location.reload()}
          className="px-xl py-md bg-primary text-[var(--color-text-on-primary)] border-none rounded-sm cursor-pointer text-sm font-medium hover:bg-primary-hover transition-colors"
        >
          Refresh Page
        </button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="text-center p-3xl bg-[var(--color-bg)] rounded-sm border border-border">
        <div className="text-5xl mb-lg text-text-light">
          🔍
        </div>
        <h3 className="font-serif text-2xl text-primary mb-md">
          No Products Found
        </h3>
        <p className="text-text-secondary mb-xl max-w-md mx-auto">
          We couldn't find any products matching your criteria.
        </p>
        <a
          href="/products"
          className="inline-block px-xl py-md bg-primary text-[var(--color-text-on-primary)] no-underline rounded-sm text-sm font-medium hover:bg-primary-hover transition-colors"
        >
          Browse Products
        </a>
      </div>
    );
  }

  return (
    <div
      className={`grid ${gridClassName || DEFAULT_GRID_CLASS} gap-lg`}
      data-testid="product-grid"
      role="list"
      aria-label="Product listings"
    >
      {products.map((product) => (
        <ProductCard key={product.id} product={product} />
      ))}
    </div>
  );
};

export default ProductGrid;